import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import URL from "./common/api/ConstantURL";
import SessionExpiredPopup from "./common/components/SessionExpiredPopup";
import { setToLocalStorage } from "./common/utils/localStorage";
import { notifyNativeAccessToken } from "./common/utils/authSession";
import { performSessionLogout } from "./common/utils/authSession";
import { applyNativeWebViewSearchParams } from "./common/utils/nativeWebViewSession";
import { getStoredUserToken, resolveProtectedRouteRedirect } from "./common/utils/sessionContext";

const PrivateRoutes = ({ children }) => {
  const [isExpired, setIsExpired] = useState(null);
  const [showSessionExpiredPopup, setShowSessionExpiredPopup] = useState(false);
  const [sessionExpiredPopupContent, setSessionExpiredPopupContent] = useState({
    header: "You are Logged Out",
    title: "Your session has expired.",
    subtitle: "You will be redirected to the Login page.",
  });
  const [isRouteReady, setIsRouteReady] = useState(false);
  const [redirectPath, setRedirectPath] = useState("");
  const [handlingSessionExpiryLogout, setHandlingSessionExpiryLogout] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isTokenExpired = (token) => {
    if (!token) return true;

    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;
      return decoded.exp < now;
    } catch {
      return true;
    }
  };

  const refreshAccessToken = async () => {
    try {
      const res = await fetch(URL.refreshAccessToken, {
        method: "GET",
        credentials: "include",
      });

      if (!res.ok) return null;

      const responseData = await res.json();
      const newToken = responseData?.data?.accessToken;

      if (newToken) {
        setToLocalStorage("user_token", newToken);
        await notifyNativeAccessToken(newToken);
        return newToken;
      }

      return null;
    } catch {
      return null;
    }
  };

  const resolveDirectPathGuards = () => {
    setRedirectPath(resolveProtectedRouteRedirect(location.pathname));
  };

  useEffect(() => {
    const checkToken = async () => {
      setIsRouteReady(false);

      let token = getStoredUserToken();
      const bootstrap = applyNativeWebViewSearchParams({
        pathname: location.pathname,
        rawSearch: window.location.search,
        isTokenExpired,
      });

      if (bootstrap.applied && bootstrap.token !== undefined) {
        token = bootstrap.token;
      }

      if (!token || isTokenExpired(token)) {
        let newToken = await refreshAccessToken();
        if (!newToken || isTokenExpired(newToken)) {
          setSessionExpiredPopupContent({
            header: "You are Logged Out",
            title: "Your session has expired.",
            subtitle: "You will be redirected to the Login page.",
          });
          setIsExpired(true);
          setShowSessionExpiredPopup(location.pathname !== "/login");
          setIsRouteReady(true);
          return;
        } else {
          setIsExpired(false);
          setShowSessionExpiredPopup(false);
        }
      } else {
        setIsExpired(false);
        setShowSessionExpiredPopup(false);
      }
      resolveDirectPathGuards();
      setIsRouteReady(true);
    };

    checkToken();
  }, [location.pathname]);

  if (isExpired === null || !isRouteReady) return null;

  if (isExpired) {
    if (location.pathname === "/login" || location.pathname === "/forgot-password" || location.pathname === "/set-password") {
      return children;
    }

    if (showSessionExpiredPopup) {
      return (
        <SessionExpiredPopup
          header={sessionExpiredPopupContent.header}
          title={sessionExpiredPopupContent.title}
          subtitle={sessionExpiredPopupContent.subtitle}
          onConfirm={async () => {
            const isNativeWebView = Boolean(window.flutter_inappwebview?.callHandler);
            setHandlingSessionExpiryLogout(true);
            await performSessionLogout({
              navigate: isNativeWebView ? undefined : navigate,
              notifyNative: true,
            });
            setShowSessionExpiredPopup(false);
            if (!isNativeWebView) {
              setHandlingSessionExpiryLogout(false);
            }
          }}
        />
      );
    }

    if (handlingSessionExpiryLogout && window.flutter_inappwebview?.callHandler) {
      return null;
    }

    return <Navigate to="/login" replace />;
  }

  if (redirectPath && redirectPath !== location.pathname) {
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default PrivateRoutes;
